import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {
  DomainPageSettings,
  DomainSettingKey,
} from '@gatewee/types/domain-setting';
import {RecipientUser} from '@gatewee/types/user';
import {AuthService} from '../../../_services/auth/auth.service';
import {SettingsService} from '@gatewee/services/core/settings.service';

@Component({
  selector: 'app-password-reset',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  standalone: false,
})
export class SignupComponent implements OnInit {
  signupForm!: FormGroup;
  hide_show = false;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('token') invitationToken?: string;
  user!: RecipientUser;

  assets!: DomainPageSettings['assets'];

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
    private fb: FormBuilder,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    if (this.invitationToken) {
      this.verifyToken();
    }
    this.initializeForm();

    this.assets = (
      this.settingsService.get(
        'branding',
        DomainSettingKey.PAGE,
      ) as DomainPageSettings
    )?.assets;
  }

  initializeForm() {
    this.signupForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      acceptTerms: [false, [Validators.requiredTrue]],
      invitationToken: [this.invitationToken],
    });
  }

  passwordHide() {
    this.hide_show = !this.hide_show;
  }

  verifyToken() {
    this.authService.verifyInvitationToken(this.invitationToken!).subscribe({
      next: (data: RecipientUser) => {
        if (data.email) {
          this.user = data;
          this.signupForm.patchValue({email: data.email});
          this.signupForm.patchValue({invitationToken: this.invitationToken});
        }
      },
      error: () => {
        void this.router.navigate(['/']).then(() => {
          this.alerts
            .open(
              this.translate.instant('features.signup.toast.invitationExpired'),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
        });
      },
    });
  }

  onSubmit() {
    if (this.signupForm.invalid) {
      this.alerts
        .open(this.translate.instant('features.signup.toast.formError'), {
          label: this.translate.instant('common.status.error'),
          appearance: 'error',
        })
        .subscribe();
      return;
    }

    const {name, email, password, invitationToken} = this.signupForm.value;
    this.authService
      .signup({name, email, password, invitationToken} as RecipientUser)
      .subscribe({
        next: () => {
          this.alerts
            .open(
              this.translate.instant(
                'features.signup.toast.registrationSuccess',
              ),
              {
                label: this.translate.instant('common.status.success'),
                appearance: 'success',
              },
            )
            .subscribe();
          this.authService.login(email, password).subscribe(
            () => {
              void this.router.navigate(['/panel']).then(() => {
                this.alerts
                  .open(
                    this.translate.instant(
                      'features.signup.toast.loginSuccess',
                    ),
                    {
                      label: this.translate.instant('common.status.success'),
                      appearance: 'success',
                    },
                  )
                  .subscribe();
              });
            },
            () => {
              this.alerts
                .open(
                  this.translate.instant('features.signup.toast.loginError'),
                  {
                    label: this.translate.instant('common.status.error'),
                    appearance: 'error',
                  },
                )
                .subscribe();
            },
          );
        },
        error: () => {
          this.alerts
            .open(
              this.translate.instant('features.signup.toast.registrationError'),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
        },
      });
  }
}
